import type { BridgeRequest, BridgeResponse } from "../shared/contracts";
import { hostedParentOrigin } from "./hosted-bridge";

const TOKEN_KEY = "411-agent-desk-token";
const AGENT_TOKEN_KEY = "411-agent-desk-agent-token";
export const isExtension =
  typeof chrome !== "undefined" &&
  Boolean(chrome.runtime?.id) &&
  location.protocol === "chrome-extension:";

export class ApiError extends Error {
  constructor(
    message: string,
    public status = 0,
  ) {
    super(message);
  }
}

export type SessionScope = "dashboard" | "agent";
const sessionKey = (scope: SessionScope) =>
  scope === "agent" ? AGENT_TOKEN_KEY : TOKEN_KEY;
// A toolbar and a drawer share the service origin, but have separate child
// sessions. Keep embedded credentials in this frame's JS realm, never storage.
let embeddedAgentToken: string | null = null;
const memorySession = (scope: SessionScope) =>
  scope === "agent" && Boolean(hostedParentOrigin);
const sessionToken = (scope: SessionScope) =>
  memorySession(scope)
    ? embeddedAgentToken
    : sessionStorage.getItem(sessionKey(scope));
export function hasWebSession(scope: SessionScope = "dashboard") {
  return Boolean(sessionToken(scope));
}
export function saveSession(token: string, scope: SessionScope = "dashboard") {
  if (isExtension) return;
  if (memorySession(scope)) embeddedAgentToken = token;
  else sessionStorage.setItem(sessionKey(scope), token);
}
export function clearSession(scope: SessionScope = "dashboard") {
  if (isExtension) return;
  if (memorySession(scope)) embeddedAgentToken = null;
  else sessionStorage.removeItem(sessionKey(scope));
}

export async function request<T>(
  method: "GET" | "POST" | "PUT" | "PATCH",
  path: string,
  body?: unknown,
  scope: SessionScope = "dashboard",
): Promise<T> {
  const timeoutMs =
    method === "POST" && path === "/api/admin/directory/sync" ? 130000 : 8000;
  if (isExtension) {
    const response = await new Promise<BridgeResponse>((resolve, reject) => {
      let settled = false;
      const timeout = window.setTimeout(() => {
        if (!settled) {
          settled = true;
          reject(new ApiError("GHL Connect request timed out"));
        }
      }, timeoutMs);
      chrome.runtime.sendMessage(
        { type: "desk:request", method, path, body } satisfies BridgeRequest,
        (result: BridgeResponse) => {
          const runtimeError = chrome.runtime.lastError;
          if (settled) return;
          settled = true;
          window.clearTimeout(timeout);
          if (runtimeError)
            reject(
              new ApiError(
                runtimeError.message ?? "Extension connection unavailable",
              ),
            );
          else resolve(result);
        },
      );
    });
    if (!response?.ok)
      throw new ApiError(
        response?.error ?? "GHL Connect request failed",
        response?.status ?? 0,
      );
    return response.data as T;
  }
  let response: Response;
  const token = sessionToken(scope);
  try {
    response = await fetch(path, {
      method,
      signal: AbortSignal.timeout(timeoutMs),
      headers: {
        ...(body === undefined ? {} : { "Content-Type": "application/json" }),
        ...(token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (cause) {
    throw new ApiError(
      cause instanceof Error && cause.name === "TimeoutError"
        ? "GHL Connect request timed out"
        : "GHL Connect connection unavailable",
    );
  }
  let data: unknown;
  try {
    data = await response.json();
    if (response.ok && (data === null || typeof data !== "object"))
      throw new Error("Incomplete response");
  } catch {
    if (response.ok)
      throw new ApiError(
        "GHL Connect returned an incomplete response. Review saved items before retrying.",
      );
    data = {};
  }
  if (!response.ok) {
    const message =
      data !== null &&
      typeof data === "object" &&
      "error" in data &&
      typeof data.error === "string" &&
      data.error
        ? data.error
        : `Request failed (${response.status})`;
    throw new ApiError(message, response.status);
  }
  return data as T;
}

export function agentRequest<T>(
  method: "GET" | "POST" | "PUT" | "PATCH",
  path: string,
  body?: unknown,
): Promise<T> {
  return request<T>(method, path, body, "agent");
}
